import { styled } from '@linaria/react';
import { Group, TextInput } from '@mantine/core';
import { ReactNode, useState } from 'react';
import type { ProjectsResponse, Subproject } from '../../../../api/projects-client/projects-client.type';
import { skyDark } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Divider } from '../../../../ui-library/divider/divider';
import { Icon } from '../../../../ui-library/icon/icon';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { Table, TableColumn } from '../../../data-management/components/table';
import { DataManagementDropShadowContainer } from '../data-management-header';
import { mapBoardToTableRow } from './projects.helpers';
import type { BoardTableRow } from './projects.type';

type ProjectPanelProps = {
  project: ProjectsResponse | null;
  boards: Subproject[];
  selectedBoard: Subproject | null;
  isEditingProject: boolean;
  setIsEditingProject: (isEditing: boolean) => void;
};

const columns: TableColumn<BoardTableRow>[] = [
  {
    key: 'name',
    label: 'Board',
    render: (_value: ReactNode, row: BoardTableRow) => (
      <div style={{ marginLeft: 8 }}>
        <Text weight="medium">{row.name}</Text>
      </div>
    ),
  },
  { key: 'configurationState', label: 'Configuration State' },
];

export function ProjectPanel({ project, boards, selectedBoard, setIsEditingProject }: ProjectPanelProps) {
  const [search, setSearch] = useState('');

  // This is here as a temporary fix to allow editing of projects which are not sourced from a PM tool.
  // BE has a list and dummy seems to be the best option for now.
  const canEdit = project && (project.pm_tool === 'dummy' || project.pm_tool === 'bloomfilter');

  const tableData = boards.map((board) => {
    return mapBoardToTableRow(board);
  });

  const filteredData = tableData.filter((item) => {
    const query = search.toLowerCase().trim();
    if (!query) {
      return true;
    }
    return Object.values(item).some((value) => value?.toString().toLowerCase().includes(query));
  });

  return (
    <DataManagementDropShadowContainer>
      {project && (
        <HeaderContainer>
          <Group gap="8px">
            <Text weight="bold" size="large">
              {project?.name}
            </Text>
          </Group>
          <Group>
            <TextInput
              placeholder="Search for Board"
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
              leftSection={<Icon name="search" size={16} color={skyDark} />}
              style={{ width: 250 }}
              radius="xl"
              size="xs"
            />
            {canEdit ? (
              <Button
                variant="primary"
                size="xs"
                radius="xl"
                leftSection={<Icon name="edit" variant="filled" size={16} color="white" />}
                style={{ padding: '8px 16px', gap: '8px' }}
                onClick={() => setIsEditingProject(true)}
              >
                <Text>Edit Project</Text>
              </Button>
            ) : (
              <Tooltip variant="white" position="top" label="Cannot edit externally sourced projects.">
                <Button
                  disabled
                  variant="primary"
                  size="xs"
                  radius="xl"
                  leftSection={<Icon name="edit" variant="filled" size={16} color="white" />}
                  style={{ padding: '8px 16px', gap: '8px', cursor: 'default' }}
                  onClick={() => {}}
                >
                  <Text>Edit Project</Text>
                </Button>
              </Tooltip>
            )}
          </Group>
        </HeaderContainer>
      )}
      <Divider style={{ marginTop: project ? 24 : 54 }} />
      {project && boards.length > 0 ? (
        <TableContainer>
          <Table
            data={filteredData}
            columns={columns}
            selectedRowId={selectedBoard?.id}
            onRowClick={() => {}}
            getRowId={(row) => row.id}
            stickyHeader
          />
        </TableContainer>
      ) : project ? (
        <EmptyStateContainer>
          <Icon name="add_circle_outlined" size={40} color={skyDark} />
          <Text size="large" color={skyDark}>
            Start adding boards to your project
          </Text>
        </EmptyStateContainer>
      ) : (
        <EmptyStateContainer>
          <Icon name="check_circle_outlined" size={40} color={skyDark} />
          <Text size="large" color={skyDark}>
            No project selected
          </Text>
        </EmptyStateContainer>
      )}
    </DataManagementDropShadowContainer>
  );
}

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const TableContainer = styled.div`
  flex: 1;
  overflow: auto;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  align-items: center;
  height: 100%;
`;
